import { useLanguage } from "@/contexts/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { User, Headphones } from "lucide-react";

const sentimentColors = {
  positive: { bg: "bg-[#00C853]/5", border: "border-[#00C853]/20", text: "text-[#00C853]" },
  neutral: { bg: "bg-[#FFB800]/5", border: "border-[#FFB800]/20", text: "text-[#FFB800]" },
  negative: { bg: "bg-[#FF2A2A]/5", border: "border-[#FF2A2A]/20", text: "text-[#FF2A2A]" },
};

export default function TranscriptViewer({ transcript, onClose }) {
  const { t } = useLanguage();

  if (!transcript) return null;

  const segments = transcript.segments || [];
  const totalDuration = transcript.duration_seconds || 60;

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  return (
    <div className="space-y-4" data-testid="transcript-viewer">
      {/* Timeline bar */}
      <div className="relative h-6 bg-[#F1F3F5] border border-[#E5E7EB]">
        {segments.map((seg, i) => {
          const start = (seg.time_sec / totalDuration) * 100;
          const width = Math.max(((segments[i + 1]?.time_sec || totalDuration) - seg.time_sec) / totalDuration * 100, 2);
          const sc = sentimentColors[seg.sentiment] || sentimentColors.neutral;
          return (
            <div
              key={i}
              className={`absolute top-0 h-full ${sc.bg} border-r ${sc.border}`}
              style={{ left: `${start}%`, width: `${width}%` }}
              title={`${formatTime(seg.time_sec)} - ${seg.sentiment}`}
            />
          );
        })}
        <div className="absolute inset-0 flex items-center justify-between px-2">
          <span className="text-[9px] font-mono text-[#9CA3AF]">00:00</span>
          <span className="text-[9px] font-mono text-[#9CA3AF]">{formatTime(totalDuration)}</span>
        </div>
      </div>

      {/* Sentiment legend */}
      <div className="flex gap-3">
        {["positive", "neutral", "negative"].map((s) => (
          <div key={s} className="flex items-center gap-1">
            <span className={`w-2.5 h-2.5 ${sentimentColors[s].bg} border ${sentimentColors[s].border}`} />
            <span className="text-[9px] font-bold uppercase text-[#9CA3AF]">{t(`client.${s}`)}</span>
          </div>
        ))}
      </div>

      {/* Segments */}
      <div className="space-y-2 max-h-[320px] overflow-y-auto thin-scrollbar">
        {segments.map((seg, i) => {
          const sc = sentimentColors[seg.sentiment] || sentimentColors.neutral;
          const isOperator = seg.speaker === "operator";
          return (
            <div
              key={i}
              className={`flex gap-3 p-3 border ${sc.bg} ${sc.border}`}
              data-testid={`transcript-segment-${i}`}
            >
              <div className={`w-7 h-7 flex items-center justify-center shrink-0 ${isOperator ? "bg-[#002FA7]" : "bg-[#0A0A0A]"}`}>
                {isOperator ? <Headphones size={12} className="text-white" /> : <User size={12} className="text-white" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] font-bold uppercase text-[#0A0A0A]">{isOperator ? "Operator" : "Caller"}</span>
                  <span className="text-[9px] font-mono text-[#9CA3AF]">{formatTime(seg.time_sec)}</span>
                  <Badge className={`rounded-none text-[8px] font-bold h-4 px-1 ${sc.text} ${sc.bg}`}>{seg.sentiment}</Badge>
                </div>
                <p className="text-xs text-[#4B5563] leading-relaxed">{seg.text}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
